/**
 * The ribbon timeline's layout (section 11.0): 8 AM to 4 PM in the tech's own
 * timezone, stretched to whole hours around anything outside that. Positions
 * are percentages of the ribbon's width, from real instants, so a day with a
 * daylight-saving change still lines up with its hour marks.
 */
import { addDays, clockIn, zonedToUtc } from "../../lib/dates";

export const DEFAULT_FIRST_HOUR = 8;
export const DEFAULT_LAST_HOUR = 16;

export interface HourMark {
  /** "8 AM", "12 PM". */
  label: string;
  /** Percent from the ribbon's left edge. */
  at: number;
}

export interface Ribbon {
  start: number;
  end: number;
  hours: HourMark[];
}

export interface Span {
  start: string;
  end: string;
}

function hourLabel(hour: number): string {
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelve)} ${hour % 24 < 12 ? "AM" : "PM"}`;
}

function hourInstant(day: string, hour: number, timeZone: string): number {
  if (hour >= 24) {
    return zonedToUtc(`${addDays(day, 1)}T00:00`, timeZone).getTime();
  }
  return zonedToUtc(`${day}T${String(hour).padStart(2, "0")}:00`, timeZone).getTime();
}

/** The ribbon for a local day holding `spans`. */
export function ribbonFor(day: string, timeZone: string, spans: readonly Span[]): Ribbon {
  const dayStart = hourInstant(day, 0, timeZone);
  const dayEnd = hourInstant(day, 24, timeZone);
  let first = DEFAULT_FIRST_HOUR;
  let last = DEFAULT_LAST_HOUR;
  for (const span of spans) {
    const start = Math.max(Date.parse(span.start), dayStart);
    if (start >= dayEnd) {
      continue;
    }
    const end = Math.max(Date.parse(span.end), start);
    first = Math.min(first, clockIn(new Date(start).toISOString(), timeZone).hour);
    if (end >= dayEnd) {
      last = 24;
      continue;
    }
    const { hour, minute } = clockIn(new Date(end).toISOString(), timeZone);
    const onTheHour = minute === 0 && end % 60_000 === 0;
    last = Math.max(last, onTheHour ? hour : hour + 1);
  }
  const start = hourInstant(day, first, timeZone);
  const end = hourInstant(day, last, timeZone);
  const hours: HourMark[] = [];
  for (let hour = first; hour <= last; hour++) {
    hours.push({ label: hourLabel(hour), at: ((hourInstant(day, hour, timeZone) - start) / (end - start)) * 100 });
  }
  return { start, end, hours };
}

/** Where a span sits on the ribbon, as percentages. Clipped to the ribbon. */
export function place(ribbon: Ribbon, start: string, end: string): { left: number; width: number } {
  const total = ribbon.end - ribbon.start;
  const from = Math.min(Math.max(Date.parse(start), ribbon.start), ribbon.end);
  const to = Math.min(Math.max(Date.parse(end), from), ribbon.end);
  return { left: ((from - ribbon.start) / total) * 100, width: ((to - from) / total) * 100 };
}

/**
 * Each block capsule's z-index, by ID (#251). The capsules share one row, so a
 * shorter one draws over a longer one, and a block inside or starting with
 * another stays reachable; the selected one draws over all. Of two the same
 * width, the later one given stays on top, as the page order would have it.
 */
export function stacking(
  capsules: readonly { id: string; width: number }[],
  selectedId: string | null,
): Map<string, number> {
  const bottomUp = capsules
    .map(({ id, width }, index) => ({ id, width, index, selected: id === selectedId }))
    .sort((a, b) => Number(a.selected) - Number(b.selected) || b.width - a.width || a.index - b.index);
  return new Map(bottomUp.map(({ id }, rank) => [id, rank + 1]));
}

/** The "now" marker's position, or null when now is off the ribbon. */
export function nowAt(ribbon: Ribbon, now: Date): number | null {
  const at = now.getTime();
  if (at < ribbon.start || at > ribbon.end) {
    return null;
  }
  return ((at - ribbon.start) / (ribbon.end - ribbon.start)) * 100;
}
